import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtTL, fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/StatusBadge";
import { Plus, Search, Edit, Trash2, Eye, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";

const STATUS_LABELS = {
  pending: { label: "Beklemede", variant: "warning" },
  shipped: { label: "Gönderildi", variant: "success" },
  returned: { label: "İade", variant: "danger" },
  cancelled: { label: "İptal", variant: "default" },
};

export default function EcommerceSales() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [q, setQ] = useState("");
  const [marketplace, setMarketplace] = useState("all");
  const [status, setStatus] = useState("all");
  const [summary, setSummary] = useState(null);

  const load = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (marketplace !== "all") params.marketplace_id = marketplace;
    if (status !== "all") params.status = status;
    const [{ data }, s] = await Promise.all([
      api.get("/ecommerce/sales", { params }),
      api.get("/ecommerce/sales-summary"),
    ]);
    setItems(data);
    setSummary(s.data);
  };

  useEffect(() => {
    api.get("/ecommerce/marketplaces").then((r) => setMarkets(r.data));
  }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, marketplace, status]);

  const remove = async (id) => {
    if (!window.confirm("Bu satışı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/ecommerce/sales/${id}`);
      toast.success("Satış silindi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  const kpi = (label, value, color) => (
    <div className="azd-card p-4">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">{label}</div>
      <div className={`text-2xl font-extrabold ${color}`}>{value}</div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {kpi("Toplam Satış", summary?.count ?? 0, "text-[#08285A]")}
        {kpi("Brüt Ciro", fmtTL(summary?.gross_total || 0), "text-[#0B3676]")}
        {kpi("Net Ciro", fmtTL(summary?.net_total || 0), "text-[#16A34A]")}
        {kpi("Toplam Kâr", fmtTL(summary?.profit_total || 0), "text-[#F59E0B]")}
      </div>

      <div className="azd-card p-4 flex flex-wrap items-end gap-3 justify-between">
        <div className="flex flex-wrap gap-3 flex-1">
          <div className="relative min-w-[240px] flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Sipariş no, müşteri veya ürün ara..."
                   className="pl-9" data-testid="ec-search" />
          </div>
          <div className="min-w-[180px]">
            <Select value={marketplace} onValueChange={setMarketplace}>
              <SelectTrigger data-testid="ec-mkt"><SelectValue placeholder="Pazaryeri" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Pazaryerleri</SelectItem>
                {markets.filter(m => m.is_active).map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="ec-status"><SelectValue placeholder="Durum" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-2">
          <ExportButton
            data={items}
            filename="e-ticaret-satislar"
            columns={[
              { key: "order_no", label: "Sipariş No" },
              { key: "customer_name", label: "Müşteri" },
              { key: "order_date", label: "Sipariş Tarihi" },
              { key: "marketplace_name", label: "Pazaryeri" },
              { key: "product_code", label: "Ürün Kodu" },
              { key: "product_name", label: "Ürün Adı" },
              { key: "quantity", label: "Adet" },
              { key: "sale_price", label: "Satış Fiyatı" },
              { key: "net_total", label: "Net Ciro" },
              { key: "profit", label: "Kâr" },
              { key: "status", label: "Durum" },
            ]}
          />
          <Button onClick={() => navigate("/ecommerce/sales/new")}
                  data-testid="ec-new"
                  className="bg-[#0B3676] hover:bg-[#08285A] text-white">
            <Plus size={16} className="mr-1" /> E-Ticaret Satışı Ekle
          </Button>
        </div>
      </div>

      <div className="azd-card overflow-hidden">
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Sipariş No</th>
                <th className="text-left px-4 py-3 font-semibold">Tarih</th>
                <th className="text-left px-4 py-3 font-semibold">Müşteri</th>
                <th className="text-left px-4 py-3 font-semibold">Pazaryeri</th>
                <th className="text-left px-4 py-3 font-semibold">Ürün</th>
                <th className="text-right px-4 py-3 font-semibold">Adet</th>
                <th className="text-right px-4 py-3 font-semibold">Net Ciro</th>
                <th className="text-right px-4 py-3 font-semibold">Kâr</th>
                <th className="text-center px-4 py-3 font-semibold">Durum</th>
                <th className="text-right px-4 py-3 font-semibold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="p-8 text-center text-[#6B7280]">
                    <ShoppingBag size={28} className="mx-auto mb-2 opacity-40" />
                    Henüz e-ticaret satışı kaydı bulunmuyor
                  </td>
                </tr>
              ) : items.map((s) => (
                <tr key={s.id} data-testid={`ec-row-${s.id}`}
                    className="border-t border-[#E5E7EB] hover:bg-[#F6F8FB]/50">
                  <td className="px-4 py-3 font-mono text-xs font-bold text-[#08285A]">{s.order_no}</td>
                  <td className="px-4 py-3 text-xs">{fmtDate(s.order_date)}</td>
                  <td className="px-4 py-3 font-semibold">{s.customer_name}</td>
                  <td className="px-4 py-3"><Badge variant="info">{s.marketplace_name}</Badge></td>
                  <td className="px-4 py-3">
                    <div className="text-xs font-mono text-[#6B7280]">{s.product_code}</div>
                    <div className="text-sm truncate max-w-xs">{s.product_name}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-bold">{s.quantity}</td>
                  <td className="px-4 py-3 text-right font-bold text-[#0B3676]">{fmtTL(s.net_total)}</td>
                  <td className={`px-4 py-3 text-right font-bold ${s.profit >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
                    {fmtTL(s.profit)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant={STATUS_LABELS[s.status]?.variant || "default"}>
                      {STATUS_LABELS[s.status]?.label || s.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => navigate(`/ecommerce/sales/${s.id}/edit`)}
                            data-testid={`ec-edit-${s.id}`}
                            className="p-1.5 text-[#6B7280] hover:text-[#0B3676] rounded"
                            title="Düzenle">
                      <Edit size={14} />
                    </button>
                    <button onClick={() => remove(s.id)}
                            data-testid={`ec-del-${s.id}`}
                            className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1"
                            title="Sil">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
